import { simplePostRequestWithBody } from "../../API/baseAPI";
import { showAlert } from "../../Util/alerts";

export class FileUploader {
    static instance = null;
    creationTime = Date.now();

    constructor(inputElement, options = {}) {
        this.options = {
            maxFiles: options.maxFiles || 1,
            allowedTypes: options.allowedTypes || ['image/*'],
            maxSize: options.maxSize || 5 * 1024 * 1024, // 5MB default
            previewContainer: options.previewContainer || null,
            onFileAdded: options.onFileAdded || null,
            onFileRemoved: options.onFileRemoved || null,
            onFileUploaded: options.onFileUploaded || null,
            uploadButton: options.uploadButton || null,
            selectButton: options.selectButton || null,
            uploadUrl: options.uploadUrl || null,
            onError: options.onError || ((error) => {console.error(error); showAlert(error, 'error')}),
        };

        if (typeof this.options.uploadButton === 'string') {
            this.options.uploadButton = document.getElementById(this.options.uploadButton);
        }

        if (typeof this.options.previewContainer === 'string') {
            this.options.previewContainer = document.getElementById(this.options.previewContainer);
        }

        if (typeof this.options.selectButton === 'string') {
            this.options.selectButton = document.getElementById(this.options.selectButton);
        }

        if (FileUploader.instance) {
            FileUploader.instance.clear();
        }

        FileUploader.instance = this;
        this.files = new Map();
        this.setupInput(inputElement);
        this.setupPreviewContainer();
    }

    setupInput(inputElement) {
        if (typeof inputElement === 'string') {
            this.inputElement = document.getElementById(inputElement);
        } else {
            this.inputElement = inputElement;
        }

        if (!this.inputElement) {
            throw new Error('Input element not found');
        }

        if (this.inputElement.tagName !== 'INPUT' || this.inputElement.type !== 'file') {
            throw new Error('Input element must be a file input');
        }

        // Set multiple if maxFiles > 1
        this.inputElement.multiple = this.options.maxFiles > 1;
        
        // Set accepted file types
        this.inputElement.accept = this.options.allowedTypes.join(',');

        this.inputElement.addEventListener('change', (event) => this.handleFiles(event.target.files));
        
        if (this.options.selectButton) {
            console.log('Select button provided, setting up click event');
            this.options.selectButton.addEventListener('click', () => this.inputElement.click());
        }

        if (this.options.uploadButton) {
            this.options.uploadButton.addEventListener('click', () => {
                if (this.creationTime != FileUploader.instance.creationTime) {
                    console.warn('FileUploader instance has changed, upload aborted');
                    return;
                }

                try {
                    this.upload();
                } catch (error) {
                    if (error && error.message) {
                        this.options.onError(error.message);
                    } else if (error) {
                        this.options.onError(error);
                    } else {
                        this.options.onError('An unknown error occurred during upload');
                    }
                }
            });
        }
    }

    setupPreviewContainer() {
        if (typeof this.options.previewContainer === 'string') {
            this.previewContainer = document.getElementById(this.options.previewContainer);
        } else {
            this.previewContainer = this.options.previewContainer;
        }

        if (!this.previewContainer) {
            this.previewContainer = document.createElement('div');
            this.previewContainer.className = 'file-uploader-preview mt-4';
            this.inputElement.parentNode.insertBefore(
                this.previewContainer, 
                this.inputElement.nextSibling
            );
        }
    }

    handleFiles(fileList) {
        const files = Array.from(fileList);

        if (this.files.size + files.length > this.options.maxFiles) {
            this.options.onError(`Maximum ${this.options.maxFiles} files allowed`);
            return;
        }

        files.forEach(file => this.addFile(file));
        this.inputElement.value = ''; // Reset input
    }

    addFile(file) {
        // Validate file
        if (!this.validateFile(file)) return;

        const fileId = crypto.randomUUID();
        this.files.set(fileId, file);

        // Create preview element
        const previewElement = this.createPreviewElement(file, fileId);
        this.previewContainer.appendChild(previewElement);

        if (this.options.onFileAdded) {
            this.options.onFileAdded(file, fileId);
        }
    }

    validateFile(file) {
        if (file.size > this.options.maxSize) {
            this.options.onError(`File ${file.name} is too large. Maximum size is ${this.formatSize(this.options.maxSize)}`);
            return false;
        }

        const fileType = file.type.split('/')[0];
        const isAllowed = this.options.allowedTypes.some(type => {
            const [category, ext] = type.split('/');
            return category === fileType || type === '*' || type === file.type;
        });

        if (!isAllowed) {
            this.options.onError(`File type ${file.type} is not allowed`);
            return false;
        }

        return true;
    }

    createPreviewElement(file, fileId) {
        const wrapper = document.createElement('div');
        wrapper.className = 'flex items-center gap-2 bg-base-200 rounded-lg p-2 mb-2';
        wrapper.dataset.fileId = fileId;

        // Icon based on file type
        const icon = document.createElement('i');
        icon.className = this.getFileIcon(file.type);
        
        // File name and size
        const info = document.createElement('div');
        info.className = 'flex-1 min-w-0';
        
        const name = document.createElement('div');
        name.className = 'truncate text-sm font-medium';
        name.textContent = file.name;
        
        const size = document.createElement('div');
        size.className = 'text-xs text-gray-500';
        size.textContent = this.formatSize(file.size);
        
        info.appendChild(name);
        info.appendChild(size);

        // Remove button
        const removeBtn = document.createElement('button');
        removeBtn.className = 'btn btn-ghost btn-sm btn-square text-error';
        removeBtn.innerHTML = '<i class="fa-solid fa-times"></i>';
        removeBtn.onclick = () => this.removeFile(fileId);

        // Preview for images
        if (file.type.startsWith('image/')) {
            const imgPreview = document.createElement('img');
            imgPreview.className = 'w-10 h-10 object-cover rounded';
            imgPreview.alt = file.name;

            const reader = new FileReader();
            reader.onload = (e) => imgPreview.src = e.target.result;
            reader.readAsDataURL(file);

            wrapper.appendChild(imgPreview);
        } else {
            wrapper.appendChild(icon);
        }

        wrapper.appendChild(info);
        wrapper.appendChild(removeBtn);

        return wrapper;
    }

    removeFile(fileId) {
        const element = this.previewContainer.querySelector(`[data-file-id="${fileId}"]`);
        if (element) {
            const file = this.files.get(fileId);
            this.files.delete(fileId);
            element.remove();

            if (this.options.onFileRemoved) {
                this.options.onFileRemoved(file, fileId);
            }
        }
    }

    getFiles() {
        return Array.from(this.files.values());
    }

    clear() {
        this.files.clear();
        this.previewContainer.innerHTML = '';
        this.inputElement.value = '';
    }

    getFileIcon(mimeType) {
        const type = mimeType.split('/')[0];
        const icons = {
            'image': 'fa-solid fa-image',
            'video': 'fa-solid fa-video',
            'audio': 'fa-solid fa-music',
            'application': 'fa-solid fa-file',
            'text': 'fa-solid fa-file-text',
            'default': 'fa-solid fa-file'
        };
        return icons[type] || icons.default;
    }

    formatSize(bytes) {
        const units = ['B', 'KB', 'MB', 'GB'];
        let size = bytes;
        let unitIndex = 0;

        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }

        return `${size.toFixed(1)} ${units[unitIndex]}`;
    }

    upload() {
        return new Promise((resolve, reject) => {
            if (this.files.size === 0) {
                reject('No files selected for upload');
                return;
            }
    
            if (!this.options.uploadUrl) {
                reject('Upload URL not provided');
                return;
            }
    
            const uploadUrl = this.options.uploadUrl;
            const uploadBtn = this.options.uploadButton || document.getElementById('uploadButton');
    
            const formData = new FormData();
            // Add all files from the Map to the FormData
            for (const [id, file] of this.files) {
                formData.append('file', file);
            }
    
            if (!uploadBtn.id) {
                uploadBtn.id = 'uploadButton';
            }

            resolve(simplePostRequestWithBody(uploadUrl, formData, uploadBtn.id, (res) => {
                if (res.error) {
                    this.options.onError(res.message? res.message : res.error);
                    return;
                } 

                if (this.options.onFileUploaded) {
                    this.options.onFileUploaded(res)
                }
                this.previewContainer.innerHTML = '';
                this.files.clear();
                this.inputElement.value = '';
                this.options.uploadButton?.classList.add('hidden');
                showAlert(t('account.fileUploaded'), 'success');
            }));
        });
    }
}